import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Helpers } from '@shared/helpers';

@Component({
  selector: 'zbp-number-input',
  templateUrl: './number-input.component.html',
  styleUrls: ['./number-input.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgbModule,
  ]
})
export class NumberInputComponent implements OnInit, AfterViewInit {
  ngOnInit(): void {
    this.applyLimitConstraints();
  }

  @Input() uniqueId: string;
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() invalid: boolean = false;
  @Input() ariaLiveCounterState: string = 'polite';
  @Input() minValue: number = undefined; // lowest value acceptable
  @Input() tooltipExceedsMin: string = undefined;
  @Input() maxValue: number = undefined; // highest value acceptable
  @Input() tooltipExceedsMax: string = undefined;
  @Input() constrainToLimits: boolean = false; // changes input to min or max if it exceeds
  @Input({ required: true }) innerFormControl: FormControl<number>;
  @Input() theme: 'default' | 'large' = 'default';
  @Input() showMax = false;
  @Input() placeholder: string = '';
  /** When allowMinusToNull is true it allows the minus button to be clicked when on 0 and null out the value */
  @Input() allowMinusToNull = false;
  @Input() allowNullValue = false;
  @Input() applyConstraintOnNullInput = true;
  @Input() counterAriaLabel: string = '';
  @Input() subtractButtonAriaLabel: string = 'minus';
  @Input() addButtonAriaLabel: string = 'plus';
  @ViewChild('counter') counter: ElementRef;
  @ViewChild('calc') calc: ElementRef;

  tooltip: string = undefined;
  activeTooltip: NgbTooltip;

  isSubtractDisabled: boolean = false;
  isAddDisabled: boolean = false;
  subtractTooltip: string = null;
  addTooltip: string = null;

  minusButtonAriaLabel: string = this.subtractButtonAriaLabel;

  ngAfterViewInit() {
    this.configureButtonStates();
  }

  onSubtractButtonClick(): void {
    if (!this.isSubtractDisabled) {
      if (this.allowMinusToNull && this.innerFormControl.value === 0) {
        this.innerFormControl.patchValue(null);
      } else {
        this.innerFormControl.patchValue(this.innerFormControl.value - 1);
      }
      this.innerFormControl.markAsTouched();
      this.innerFormControl.markAsDirty();
      this.applyLimitConstraints();
    }
  }

  onAddButtonClick(): void {
    if (!this.isAddDisabled) {
      if (this.innerFormControl.value === null) {
        this.innerFormControl.patchValue(0);
      } else {
        this.innerFormControl.patchValue(this.innerFormControl.value + 1);
      }
      this.innerFormControl.markAsTouched();
      this.innerFormControl.markAsDirty();
      this.applyLimitConstraints();
    }
  }

  onBlur(): void {
    this.applyLimitConstraints();
  }

  onScroll(event: Event): void {
    event.preventDefault();
  }

  onInputChange(): void {
    if (this.applyConstraintOnNullInput || !Helpers.isNullOrUndefined(this.innerFormControl.value)) {
      this.applyLimitConstraints();
    } else {
      this.configureButtonStates();
      this.recalculateInputWidth();
    }
  }

  applyLimitConstraints(): void {
    // set the value of the control to its number value - this will remove leading zeroes
    this.innerFormControl.setValue(this.innerFormControl.value);

    if (this.constrainToLimits) {
      // if the value is null and allowNullValue is false, we should force the value to the minimum
      if (!Helpers.isNullOrUndefined(this.minValue)
        && ((!this.allowNullValue && Helpers.isNullOrUndefined(this.innerFormControl.value))
          || (!Helpers.isNullOrUndefined(this.innerFormControl.value)
            && this.innerFormControl.value < this.minValue))) {
        this.innerFormControl.setValue(this.minValue);
      }
      if (!Helpers.isNullOrUndefined(this.maxValue) && this.innerFormControl.value > this.maxValue) {
        this.innerFormControl.setValue(this.maxValue);
      }
    }

    this.configureButtonStates();
    this.recalculateInputWidth();
  }

  recalculateInputWidth(): void {
    if (this.counter && this.calc) {
      // renders counter text in an offscreen span to appropriately size itself.
      // eslint-disable-next-line no-param-reassign
      this.calc.nativeElement.innerHTML = this.counter.nativeElement.value.replace(/\s/g, '&nbsp;') ?? '';
      // eslint-disable-next-line no-param-reassign
      this.counter.nativeElement.style.width = `${this.calc.nativeElement.offsetWidth}px`;
    }
  }

  subtractButtonTooltip(): string | undefined {
    if (this.tooltipExceedsMin && this.isSubtractDisabledCheck() && this.minValue >= 0) {
      this.tooltip = this.tooltipExceedsMin;
      this.minusButtonAriaLabel = this.tooltipExceedsMin;
      return this.tooltipExceedsMin;
    }
    this.minusButtonAriaLabel = this.subtractButtonAriaLabel;
    return undefined;
  }

  addButtonTooltip(): string | undefined {
    if (this.tooltipExceedsMax && this.isAddDisabledCheck()) {
      this.tooltip = this.tooltipExceedsMax;
      return this.tooltipExceedsMax;
    }
    return undefined;
  }

  isAddDisabledCheck(): boolean {
    return (this.disabled
      || (this.innerFormControl.disabled)
      || (this.innerFormControl.value >= this.maxValue));
  }

  isSubtractDisabledCheck(): boolean {
    return (this.disabled
      || (this.innerFormControl.disabled)
      || (this.innerFormControl.value === null)
      || (this.innerFormControl.value === undefined)
      || (!this.allowMinusToNull && this.innerFormControl.value <= this.minValue));
  }

  showTip(t: NgbTooltip) {
    t.open();
    this.activeTooltip = t;
    this.minusButtonAriaLabel = this.subtractButtonAriaLabel;
  }

  closeTip() {
    if (this.activeTooltip) {
      this.activeTooltip.close();
      if (this.subtractTooltip) {
        this.minusButtonAriaLabel = this.subtractTooltip;
      }
    }
  }

  private configureButtonStates() {
    this.isAddDisabled = this.isAddDisabledCheck();
    this.addTooltip = this.addButtonTooltip();

    this.isSubtractDisabled = this.isSubtractDisabledCheck();
    this.subtractTooltip = this.subtractButtonTooltip();
  }

  protected readonly Boolean = Boolean;
}
